import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserPlus, Lock, Eye, EyeOff, ChevronRight, Check, AlertCircle, ArrowLeft, ArrowRight } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import axios from 'axios';
import MaskedTextInput from '../ui/MaskedPasswordInput'
import caregiverService from '../../services/caregiverService';
import { cleanSignInIdInput, looksLikeCaregiverId, parseCaregiverId } from '../../utils/signInId';

const ParticipantHome = () => {
    console.log('[LOGIN] Component rendering');
    const mountCount = useRef(0);
    const apiUrl = import.meta.env.VITE_API_URL || '';
    
    // Get study ID from URL parameter or subdomain
    const { studyId: urlStudyId } = useParams();
    const [studyId, setStudyId] = useState(urlStudyId || '');
    
    // Extract study from subdomain if needed
    useEffect(() => {
        // If no studyId in URL params, try to extract from subdomain
        if (!urlStudyId) {
            const hostname = window.location.hostname;
            const hostParts = hostname.split('.');
            
            // Check if this is a subdomain
            if (hostParts.length > 2 && hostParts[0] !== 'www') {
                const subdomainStudy = hostParts[0];
                console.log('[LOGIN] Extracted study from subdomain:', subdomainStudy);
                setStudyId(subdomainStudy);
            }
        }
    }, [urlStudyId]);
    
    useEffect(() => {
        mountCount.current += 1;
        console.log('[LOGIN] Component mounted, mount count:', mountCount.current);
        console.log('[LOGIN] Current study ID:', studyId);
        return () => {
            console.log('[LOGIN] Component unmounting');
        };
    }, [studyId]);

    // Login states
    const [participantNumber, setParticipantNumber] = useState('');
    const [pin, setPin] = useState('');
    // FECOA-2156: participants keep their number keypad; a caregiver switches
    // the ID field to a Caregiver ID, and one typed on a keyboard switches it too.
    const [caregiverEntry, setCaregiverEntry] = useState(false);
    const caregiverSignIn = caregiverEntry || looksLikeCaregiverId(participantNumber);
    // A participant's PIN is digits only. The field keeps what was typed, so a
    // caregiver's PIN with letters survives an edit to the ID.
    const participantPin = pin.replace(/\D/g, '');
    const [showPin, setShowPin] = useState(false);
    const [error, setError] = useState(null);
    const [networkError, setNetworkError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    
    // First login states
    const [isFirstLogin, setIsFirstLogin] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const [step, setStep] = useState('login'); // login, verify, createPin
    const [newPin, setNewPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [pinError, setPinError] = useState('');
    const [participantId, setParticipantId] = useState('');
    
    // Hardcoded study code for this site (will be used by backend to find correct site/study)
    const STUDY_CODE = "test-study-1";
    
    // Study portal customization
    const [portalTitle, setPortalTitle] = useState(null);
    const [portalDescription, setPortalDescription] = useState(null);
    const [showStudyCode, setShowStudyCode] = useState(true);
    
    // Refs for auto-focus
    const pinInputRef = useRef(null);
    const idInputRef = useRef(null);
    
    const { participantLogin, finalizeParticipantSession } = useContext(AuthContext);
    const navigate = useNavigate();

    // Clear error when inputs change
    useEffect(() => {
        if (error) {
            console.log('[LOGIN] Clearing error state due to input change');
            setError(null);
        }
    }, [participantNumber, pin]);

    // Clear pin error when pin inputs change
    useEffect(() => {
        if (pinError) {
            setPinError('');
        }
    }, [newPin, confirmPin]);
    
    // Reset loading state when step changes to createPin
    useEffect(() => {
        if (step === 'createPin') {
            setLoading(false);
        }
    }, [step]);

    // Fetch study portal customization on mount
    useEffect(() => {
        const fetchPortalInfo = async () => {
            const studyCode = getStudyCodeFromURL();
            if (!studyCode || studyCode === STUDY_CODE) return; // Skip if no real study code
            
            try {
                const response = await axios.get(`${apiUrl}/api/participant/study-info?code=${encodeURIComponent(studyCode)}`);
                if (response.data.found) {
                    setPortalTitle(response.data.participant_portal_title);
                    setPortalDescription(response.data.participant_portal_description);
                    setShowStudyCode(response.data.participant_portal_show_study_code !== false);
                    console.log('[LOGIN] Loaded portal customization:', response.data);
                }
            } catch (err) {
                console.log('[LOGIN] Could not fetch portal info (using defaults):', err.message);
            }
        };
        
        fetchPortalInfo();
    }, [apiUrl]);

    const getStudyCodeFromURL = () => {
        var parts = document.location.href.split('#')
        if (parts.length < 2)
            return STUDY_CODE;
        return parts[1].toLowerCase();
    }

    // Login process
    const proceedWithLogin = async () => {
        setLoading(true);
        setError(null);
        
        try {
            console.log('[LOGIN] Attempting login with:', {
                study: getStudyCodeFromURL(),
                participantNumber,
                pin,
                pinType: typeof pin,
                pinValue: pin
            });
            
            // Call the participantLogin function with the correct parameters
            await participantLogin(participantNumber, pin, getStudyCodeFromURL());
        } catch (err) {
            console.error('[LOGIN] Error:', {
                error: err,
                response: err.response?.data,
                status: err.response?.status
            });
            setError('Invalid participant ID or PIN');
            setLoading(false);
        }
    };

    // Check if this is the first login for a participant
    const checkFirstLogin = async () => {
        setLoading(true);
        // A caregiver signed in earlier in this tab keeps a session that would
        // otherwise ride along on the participant's requests.
        if (sessionStorage.getItem('caregiverSession')) caregiverService.logout();
        
        try {
            console.log('[LOGIN] Attempting login with:', {
                study: getStudyCodeFromURL(),
                participantNumber,
                pin
            });
            
            // Call the regular login function to verify credentials
            const response = await axios.post(`${apiUrl}/api/participant/login`, {
                study_code: getStudyCodeFromURL(),
                participant_number: participantNumber,
                pin: participantPin
            });
            
            console.log('[LOGIN] Login response:', response.data);
            
            // Check if this is first login based on the response
            if (response.data.is_first_login) {
                console.log('[LOGIN] First login detected');
                setIsFirstLogin(true);
                
                // Store the participant ID from the response
                if (response.data.participant_id) {
                    console.log('[LOGIN] Storing participant ID for PIN setting:', response.data.participant_id);
                    setParticipantId(response.data.participant_id);
                }
                
                // Go directly to PIN creation step
                setPin(''); // Clear PIN field for new PIN creation
                setStep('createPin');
                setLoading(false);
            } else {
                // Not first login — reuse the tokens/user from the call we just made instead of
                // re-hitting /api/participant/login/ (that was ~1s of wasted round-trip before).
                console.log('[LOGIN] Regular login, finalizing session from first-login check');
                finalizeParticipantSession(response.data);
            }
        } catch (err) {
            console.error('[LOGIN] Login error:', err);
            setError('Invalid participant ID or PIN. Please check your credentials and try again.');
            setLoading(false);
        }
    };

    // Set the participant's PIN during first login
    const setPinForParticipant = async () => {
        // Validate PIN - always ensure PINs match regardless of visibility
        if (newPin !== confirmPin) {
            setPinError('PINs do not match');
            return;
        }
        
        if (newPin.length < 6) {
            setPinError('PIN must be at least 6 digits');
            return;
        }
        
        // Ensure PIN contains only digits
        if (!/^\d+$/.test(newPin)) {
            setPinError('PIN must contain only numbers');
            return;
        }
        
        // Check if PIN is all the same digit (e.g., 666666)
        if (/^(\d)\1+$/.test(newPin)) {
            setPinError('PIN cannot be all the same digit (e.g., 666666)');
            return;
        }
        
        // Check if PIN is a simple sequence
        if (/^(?:012345|123456|234567|345678|456789|567890)$/.test(newPin)) {
            setPinError('PIN cannot be a simple sequence (e.g., 123456)');
            return;
        }
        
        // Check if PIN matches participant number
        if (newPin === participantNumber) {
            setPinError('PIN cannot be the same as your Participant ID');
            return;
        }
        
        setLoading(true);
        setPinError('');
        
        try {
            // Just double-check length to be safe
            if (newPin.length < 6) {
                setPinError('PIN must be at least 6 digits');
                setLoading(false);
                return;
            }

            console.log('[LOGIN] Setting PIN for participant number:', participantNumber, 'in study:', getStudyCodeFromURL());
            
            // The clinician-provided PIN was already verified in the login step
            // We just need to set the new PIN
            const response = await axios.post(`${apiUrl}/api/participant/set_pin`, {
                study_code: getStudyCodeFromURL(),
                participant_number: participantNumber,
                new_pin: newPin, // Send the new PIN to be set
                participant_id: participantId // Add participant_id as backup identification
            });
            
            console.log('[LOGIN] PIN set successfully, response:', response.data);
            
            // Add a short delay to allow the backend to complete its transaction
            // This helps avoid race conditions where the login happens before the PIN setting transaction is complete
            console.log('[LOGIN] Adding a short delay to ensure database transaction completes');
            
            // Wait for a short delay (1 second) before attempting login
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            console.log('[LOGIN] Delay complete, now attempting login with new PIN');
            
            // Directly call participantLogin with the new PIN instead of updating state
            await participantLogin(participantNumber, newPin, getStudyCodeFromURL());
            
        } catch (error) {
            console.error('[LOGIN] PIN setup error:', error);
            
            // Extract specific error message from the response if available
            let errorMsg = 'Unable to set PIN. Please try again.';
            
            if (error.response) {
                console.error('[LOGIN] Server response:', error.response.status, error.response.data);
                
                // Try to get more specific error message
                if (error.response.data && error.response.data.error) {
                    errorMsg = error.response.data.error;
                } else if (error.response.data && error.response.data.detail) {
                    errorMsg = error.response.data.detail;
                } else if (error.response.status === 500) {
                    errorMsg = 'Server error occurred while setting PIN. Please try again or contact support.';
                } else if (error.response.status === 400) {
                    errorMsg = 'Invalid PIN. Please verify your clinician-provided PIN and try again.';
                }
            } else if (error.request) {
                // The request was made but no response was received
                errorMsg = 'No response from server. Please check your connection and try again.';
            } else {
                // Client-side error
                console.error('[LOGIN] Client-side PIN setup error:', error);
                errorMsg = 'An unexpected error occurred. Please try again.';
            }
            
            setPinError(errorMsg);
            setLoading(false);
        }
    };

    // FECOA-2156: caregivers sign in here too, with the same study link and their
    // Caregiver ID. Their session is the caregiver one (/caregiver/*), and it
    // takes over from this page once they are signed in.
    const signInAsCaregiver = async (caregiverId) => {
        setLoading(true);
        setError(null);

        try {
            const result = await caregiverService.login({
                caregiver_id: caregiverId,
                pin,
                study_code: getStudyCodeFromURL(),
            });
            navigate(result?.requires_pin_change ? '/caregiver/first-login' : '/caregiver/dashboard');
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.error || err?.response?.data?.detail;
            if (status === 423) {
                setError(detail || 'Account locked. Please contact your study coordinator to reset your PIN.');
            } else if (status === 401) {
                // A failed sign-in never says which part was wrong.
                setError('Invalid Study Code, Caregiver ID or PIN.');
            } else if (!err?.response) {
                setError('Unable to reach the server. Please check your connection and try again.');
            } else {
                setError(detail || 'Unable to sign in. Please try again.');
            }
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (step === 'login') {
            if (caregiverSignIn) {
                const caregiverId = parseCaregiverId(participantNumber);
                if (!caregiverId) {
                    setError('Your Caregiver ID starts with CG-, for example CG-00001.');
                } else if (!pin) {
                    setError('Please enter both your Caregiver ID and PIN');
                } else {
                    signInAsCaregiver(caregiverId);
                }
            } else if (participantNumber && participantPin) {
                // Verify credentials and check first login status
                checkFirstLogin();
            } else {
                setError('Please enter both Participant ID and PIN');
            }
        } else if (step === 'createPin') {
            // Handle PIN creation form
            setPinForParticipant();
        }
    };
    


    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Only allow numeric input for all fields, except a Caregiver ID and
        // its PIN, which may contain letters (FECOA-2156)
        const numericValue = value.replace(/\D/g, '');

        if (name === 'participantNumber') {
            setParticipantNumber(cleanSignInIdInput(value));
        } else if (name === 'pin') {
            setPin(value);
        } else if (name === 'newPin') {
            setNewPin(numericValue);
        } else if (name === 'confirmPin') {
            setConfirmPin(numericValue);
        }
        
        if (error) {
            setError(null);
        }
        
        if (pinError) {
            setPinError('');
        }
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: { 
                type: "spring",
                stiffness: 100,
                damping: 10
            }
        }
    };

    const switchIdKind = () => {
        setCaregiverEntry(!caregiverSignIn);
        setParticipantNumber('');
        setError(null);
        idInputRef.current?.focus();
    };

    const renderLoginStep = () => {
        return (
        <>
            <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {portalTitle || 'Participant Portal'}
                </h2>
                {showStudyCode && (
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                        Study: <span className="font-semibold text-primary-700">{getStudyCodeFromURL().toUpperCase()}</span>
                    </p>
                )}
                {portalDescription && (
                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                        {portalDescription}
                    </p>
                )}
                <p className="mt-4 text-sm font-medium text-gray-700 dark:text-gray-200">
                    Sign in to your account
                </p>
            </div>
            <form className="space-y-5" onSubmit={handleSubmit}>
                {/* Participant ID field. Caregivers switch it to their
                    Caregiver ID (FECOA-2156), which takes letters too. */}
                <div>
                    <label htmlFor="participant-number" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                        {caregiverSignIn ? 'Caregiver ID' : 'Participant ID'}
                    </label>
                    <div className="relative rounded-md shadow-sm dark:shadow-gray-900/20">
                        <input
                            id="participant-number"
                            name="participantNumber"
                            ref={idInputRef}
                            type="text"
                            inputMode={caregiverSignIn ? 'text' : 'numeric'}
                            pattern={caregiverSignIn ? undefined : '\\d*'}
                            autoCapitalize={caregiverSignIn ? 'characters' : undefined}
                            spellCheck={false}
                            autoComplete="off"
                            aria-describedby={caregiverSignIn ? 'participant-number-help' : undefined}
                            required
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:shadow-gray-900/20 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            placeholder={caregiverSignIn ? 'CG-00001' : 'Enter your ID'}
                            value={participantNumber}
                            onChange={handleInputChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && pinInputRef.current) {
                                    e.preventDefault();
                                    pinInputRef.current.focus();
                                }
                            }}
                        />
                        <UserPlus className="absolute right-3 top-2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                    </div>
                    {caregiverSignIn && (
                        <p id="participant-number-help" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Your Caregiver ID starts with CG-.
                        </p>
                    )}
                    <button
                        type="button"
                        onClick={switchIdKind}
                        className="mt-2 text-sm font-medium text-primary-700 hover:text-primary-800 dark:text-primary-400"
                    >
                        {caregiverSignIn ? 'Signing in as a participant?' : 'Signing in as a caregiver?'}
                    </button>
                </div>

                {/* PIN input - underneath */}
                <div>
                    <label htmlFor="pin" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                        PIN
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm dark:shadow-gray-900/20">
                        <div className="relative">
                            <MaskedTextInput
                                id="pin"
                                name="pin"
                                show={showPin}
                                // A caregiver's PIN may contain letters (FECOA-2156).
                                inputMode={caregiverSignIn ? 'text' : 'numeric'}
                                pattern={caregiverSignIn ? undefined : '\\d*'}
                                autoComplete="current-password"
                                required
                                ref={pinInputRef}
                                className="appearance-none block w-full pr-10 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:shadow-gray-900/20 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                placeholder="••••"
                                value={caregiverSignIn ? pin : participantPin}
                                onChange={handleInputChange}
                                minLength={6}
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                                onClick={() => setShowPin(!showPin)}
                                tabIndex="-1"
                            >
                                {showPin ? (
                                    <EyeOff className="h-5 w-5" />
                                ) : (
                                    <Eye className="h-5 w-5" />
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Forgot PIN link */}
                <div className="flex items-center justify-between">
                    <div></div>
                    <div className="text-sm">
                        <span className="text-gray-500 dark:text-gray-400">
                            Forgot your PIN? Please contact your clinician or study administrator
                        </span>
                    </div>
                </div>

                {/* Error message */}
                {(error || networkError) && (
                    <div className={`rounded-md p-4 border ${networkError && !networkError.includes('error') ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`} role="alert">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <AlertCircle className={`h-5 w-5 ${networkError && !networkError.includes('error') ? 'text-green-400' : 'text-red-400'}`} />
                            </div>
                            <div className="ml-3">
                                <p className={`text-sm ${networkError && !networkError.includes('error') ? 'text-green-800' : 'text-red-800'}`}>
                                    {error || networkError}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Submit button */}
                <div>
                    <button
                        type="submit"
                        className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm dark:shadow-gray-900/20 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition duration-150 ease-in-out"
                        disabled={loading}
                    >
                        {loading ? 'Signing in...' : 'Sign in'}
                        {!loading && <ChevronRight className="ml-2 h-5 w-5" />}
                    </button>
                </div>
            </form>
        </>
        );
    };

    const renderFirstLoginVerifyStep = () => (
        <>
            <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">First Time Login</h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                    Study: <span className="font-semibold text-primary-700">{getStudyCodeFromURL().toUpperCase()}</span>
                </p>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                    Please enter your clinician-provided PIN to continue
                </p>
            </div>
            <form className="space-y-5" onSubmit={handleSubmit}>
                {/* Study Code (readonly) */}
                <div>
                    <label htmlFor="study-code" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                        Study Code
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm dark:shadow-gray-900/20">
                        <input
                            id="study-code"
                            type="text"
                            readOnly
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 rounded-md shadow-sm dark:shadow-gray-900/20 text-gray-500 dark:text-gray-400"
                            value={getStudyCodeFromURL()}
                        />
                    </div>
                </div>

                {/* Participant Number (readonly) */}
                <div>
                    <label htmlFor="participant-number" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                        Participant Number
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm dark:shadow-gray-900/20">
                        <input
                            id="participant-number"
                            type="text"
                            readOnly
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 rounded-md shadow-sm dark:shadow-gray-900/20 text-gray-500 dark:text-gray-400"
                            value={participantNumber}
                        />
                    </div>
                </div>

                {/* Default PIN input */}
                <div>
                    <label htmlFor="default-pin" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                        Clinician-Provided PIN
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm dark:shadow-gray-900/20">
                        <MaskedTextInput
                            id="default-pin"
                            name="pin"
                            show={showPin}
                            required
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:shadow-gray-900/20 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            placeholder="Enter your clinician-provided PIN"
                            value={pin}
                            onChange={handleInputChange}
                            minLength={6}
                        />
                        <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => setShowPin(!showPin)}
                        >
                            {showPin ? (
                                <Eye className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                            ) : (
                                <EyeOff className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Error message */}
                {error && (
                    <div className="rounded-md bg-red-50 p-4 border border-red-300" role="alert">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <AlertCircle className="h-5 w-5 text-red-400" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-800">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Navigation buttons */}
                <div className="flex justify-between">
                    <button
                        type="button"
                        onClick={() => {
                            setStep('login');
                            setPin('');
                            setIsFirstLogin(false);
                        }}
                        className="flex items-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:shadow-gray-900/20 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition duration-150 ease-in-out"
                    >
                        <ArrowLeft className="mr-2 h-5 w-5" />
                        Back
                    </button>
                    <button
                        type="submit"
                        className="flex items-center py-2 px-4 border border-transparent rounded-md shadow-sm dark:shadow-gray-900/20 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition duration-150 ease-in-out"
                        disabled={loading}
                    >
                        {loading ? 'Verifying...' : 'Continue'}
                        <ArrowRight className="ml-2 h-5 w-5" />
                    </button>
                </div>
            </form>
        </>
    );

    const renderCreatePinStep = () => (
        <>
            <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create Your PIN</h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                    Please create a new PIN that you'll remember.
                    <br />
                    <span className="text-gray-500 dark:text-gray-400">Your PIN should be 6-8 digits and should not be easily guessable</span>
                </p>
            </div>
            <form className="space-y-5" onSubmit={handleSubmit}>
                {/* New PIN input */}
                <div>
                    <label htmlFor="new-pin" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                        New PIN
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm dark:shadow-gray-900/20">
                        <MaskedTextInput
                            id="new-pin"
                            name="newPin"
                            show={showPin}
                            required
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:shadow-gray-900/20 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            placeholder="Enter 6-8 digit PIN"
                            value={newPin}
                            onChange={handleInputChange}
                            minLength={6}
                            pattern="[0-9]*"
                            inputMode="numeric"
                        />
                        <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => setShowPin(!showPin)}
                        >
                            {showPin ? (
                                <Eye className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                            ) : (
                                <EyeOff className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Confirm PIN input - always shown */}
                <div>
                    <label htmlFor="confirm-pin" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                        Confirm PIN
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm dark:shadow-gray-900/20">
                        <MaskedTextInput
                            id="confirm-pin"
                            name="confirmPin"
                            show={showPin}
                            required
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:shadow-gray-900/20 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            placeholder="Confirm 6-8 digit PIN"
                            value={confirmPin}
                            onChange={handleInputChange}
                            minLength={6}
                            maxLength={8}
                            pattern="[0-9]*"
                            inputMode="numeric"
                        />
                    </div>
                </div>

                {/* Important note */}
                <div className="bg-yellow-50 border border-yellow-300 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <AlertCircle className="h-5 w-5 text-yellow-400" />                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-yellow-800">
                                <strong>Important:</strong> Please write down your PIN and keep it in a safe place. If you forget your PIN, contact your study coordinator.
                            </p>
                        </div>
                    </div>
                </div>

                {/* PIN error message */}
                {pinError && (
                    <div className="rounded-md bg-red-50 p-4 border border-red-300" role="alert">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <AlertCircle className="h-5 w-5 text-red-400" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-800">{pinError}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Navigation buttons */}
                <div className="flex justify-between">
                    <button
                        type="button"
                        onClick={() => {
                            setStep('login');
                            setNewPin('');
                            setConfirmPin('');
                        }}
                        className="flex items-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:shadow-gray-900/20 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition duration-150 ease-in-out"
                    >
                        <ArrowLeft className="mr-2 h-5 w-5" />
                        Back to Login
                    </button>
                    <button
                        type="submit"
                        className="flex items-center py-2 px-4 border border-transparent rounded-md shadow-sm dark:shadow-gray-900/20 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition duration-150 ease-in-out"
                        disabled={loading}
                    >
                        {loading ? 'Setting PIN...' : 'Set PIN'}
                        <Check className="ml-2 h-5 w-5" />
                    </button>
                </div>
            </form>
        </>
    );

    return (
        <div className="force-light-mode min-h-screen flex flex-col justify-start pt-20 px-4 sm:px-6 lg:px-8">
            <motion.div 
                className="w-full max-w-md mx-auto"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-3xl overflow-hidden shadow-xl dark:shadow-gray-900/20">
                    <div className="px-8 py-10">
                        {step === 'login' && renderLoginStep()}
                        {step === 'verify' && renderFirstLoginVerifyStep()}
                        {step === 'createPin' && renderCreatePinStep()}
                    </div>
                </div>
            </motion.div>
        </div>
    );
}

export default ParticipantHome;